// TODO: This is a hot mess that needs to be removed. RCC is dead for 6 years.
/* eslint-disable complexity */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
// @ts-strict-ignore
import { inject, Inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router } from '@angular/router';

import { LoggerService } from 'src/app/core/services/logger/logger.service';
import { AppRoutePath } from 'src/app/routing/app-route-path.enum';
import { URL_PATH_SUMMARY } from 'src/app/utils/global-constants';
import { Subprocess } from 'src/app/utils/models/subprocess.enum';
import { WINDOW } from 'src/app/utils/window.token';

import { AppRouteQueryParam } from '../app-route-query-params';

import { rccPages, RccUrlData, validRccQueryParams } from './rcc-redirect.utils';

@Injectable({
  providedIn: 'root'
})
export class RccUrlRedirectGuard {
  public constructor(
    private readonly router: Router,
    private readonly loggerService: LoggerService,
    @Inject(WINDOW) private readonly window: Window
  ) {}

  public async canActivate(route: ActivatedRouteSnapshot): Promise<boolean> {
    const data: RccUrlData = this.crawlRouteForRequestData(route);
    const routingParams = this.getNewRoutingParams(data);

    if (!routingParams) {
      return false;
    }

    return this.router.navigate(
      routingParams.filter(segment => !!segment),
      { queryParams: data.queryParams }
    );
  }

  /**
   * take collected information and create parameters based on entry-method,
   * e.g via baumuster OR vehicleId OR onlineCode OR vehicleBody/-class
   */
  private getNewRoutingParams(data: RccUrlData): string[] {
    const urlSegments: string[] = [];

    // page is essential and first pathParam
    urlSegments.push(
      data.page || AppRoutePath.Motorization,
      data.context.subprocess || Subprocess.CCci,
      data.context.country.toUpperCase(),
      data.context.language.toLowerCase()
    );

    // revolve data; start with most precise entry; last parameter is always configurationStep
    if (data.vehicleId) {
      return [...urlSegments, data.vehicleId, data.configurationStep.toLowerCase()];
    }

    // make use of onlineCode
    if (data.onlineCode) {
      return [...urlSegments, data.onlineCode];
    }

    // start with vehicle model/body
    if (data.vehicleClass && data.vehicleBody) {
      return [...urlSegments, data.vehicleClass, data.vehicleBody];
    }

    // go to baumuster motorization page;
    if (data.baumuster) {
      return [...urlSegments, 'bm', data.baumuster.toString()];
    }

    // show warning, go on (probably to error 404)
    this.loggerService.warn(`Could not resolve RCC URL: ${this.window.location.href}`);
    return null;
  }

  /**
   * analyze current route and restructure all needed infos for further processes
   */
  private crawlRouteForRequestData(route: ActivatedRouteSnapshot): RccUrlData {
    let resolvedData: RccUrlData = { context: null, page: null, queryParams: {} };

    // get basic context
    resolvedData.context = {
      subprocess: route.queryParams.subprocess,
      country: /\w{2}/.test(route.url[1].path) ? route.url[1].path : null,
      language: /\w{2}/.test(route.url[2].path) ? route.url[2].path : null
    };

    /**
     * As of OWCC-2018 we have no hard-coded steps anymore so no way to check if the fragment is a valid step, validation thus takes place in configuration component
     */
    if (!!route.fragment && route.fragment.length > 0) {
      resolvedData.configurationStep = route.fragment;
    }

    // grab vehicleId by the GET-parameter; given vehicleId means starting in config
    resolvedData.vehicleId = route.queryParams.vehicleId || route.queryParams.rccVehicleId;

    // look for onlineCode
    resolvedData.onlineCode = route.queryParams.Mcid;

    // recieve baumuster
    resolvedData.baumuster = route.queryParams.rccVehicleModelId || route.queryParams.model;

    // get model and its body xD
    resolvedData.vehicleClass =
      route.queryParams.rccVehicleClassId || route.queryParams.vehicleClassId || route.queryParams.vehicleClass;
    resolvedData.vehicleBody =
      route.queryParams.rccVehicleBodyId || route.queryParams.vehicleBodyId || route.queryParams.vehicleBody;

    // if retailer, get data
    for (const entry of Object.entries(route.queryParams)) {
      const key = entry.shift() as AppRouteQueryParam;
      if (validRccQueryParams.includes(key)) {
        resolvedData.queryParams[key] = entry.shift();
      }
    }

    // determine page by given urlSegement; fallback to Motorization-page
    resolvedData = Object.assign(resolvedData, this.determinePage(route, resolvedData));

    return resolvedData;
  }

  /**
   * depending on given information, different pages are used as 'landing'-page,
   * therefore analyse and return page/configurationStep based on that info
   */
  private determinePage(
    route: ActivatedRouteSnapshot,
    data: RccUrlData
  ): {
    page?: string;
    configurationStep?: string;
  } {
    const givenPage = route.url.at(-1).path;
    const pageData: {
      page?: string;
      configurationStep?: string;
    } = {};

    // if onlineCode entry, resolve to special onlineCode route
    if (data.onlineCode) {
      pageData.page = AppRoutePath.OnlineCode;

      // summary in OWCC equals configurationPage with step 'summary'
    } else if (givenPage.toUpperCase() === URL_PATH_SUMMARY.toUpperCase()) {
      pageData.page = AppRoutePath.Configuration;
      pageData.configurationStep = URL_PATH_SUMMARY.toLowerCase();

      // vehicleId should open on configurationPage, because you cannot open motorization with vehicleId
    } else if (data.vehicleId) {
      pageData.page = AppRoutePath.Configuration;

      // if page is given, use given page or fallback, which is motorization
    } else {
      pageData.page = rccPages[givenPage] ?? AppRoutePath.Motorization;
    }

    return pageData;
  }
}

export const canActivateRccUrlRedirectGuard: CanActivateFn = async (activatedRouteSnapshot: ActivatedRouteSnapshot) =>
  inject(RccUrlRedirectGuard).canActivate(activatedRouteSnapshot);
